import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Dialog, DialogBody, DialogFooter } from '@blueprintjs/core';
import { FifoLogger } from 'fifo-logger';
import { Formik } from 'formik';
import debounce from 'lodash/debounce';
import { resurrect } from 'nmr-processing';
import { useEffect, useMemo, useRef, useState } from 'react';
import * as yup from 'yup';
import { useDispatch } from '../context/DispatchContext';
import { useToaster } from '../context/ToasterContext';
import Button from '../elements/Button';
import { GroupPane } from '../elements/GroupPane';
import ReactTable from '../elements/ReactTable/ReactTable';
import FormikTextarea from '../elements/formik/FormikTextarea';
const validationSchema = yup.object({
    publicationText: yup.string().required(),
});
function handleRowStyle(data) {
    const level = (data?.original).level;
    let backgroundColor = 'lightgreen';
    if (level > 40) {
        backgroundColor = 'pink';
    }
    else if (level === 40) {
        backgroundColor = 'lightyellow';
    }
    return { base: { backgroundColor } };
}
const INITIAL_VALUES = {
    publicationText: '1H NMR (CDCl3, 400MHz) δ 10.58 (b, 1H), 7.40 (d, 1H, J = 8.0 Hz), 6.19 (d, 1H, J = 7.6 Hz), 4.88 (s, 1H), 2.17 (s, 3H), 1.02 (s, 9H), 1.01 (s, 9H), 0.89 (s, 9H)',
};
export function ImportPublicationStringModal(props) {
    const { onClose, isOpen } = props;
    if (!isOpen)
        return;
    return _jsx(InnerImportPublicationStringModal, { onClose: onClose });
}
function InnerImportPublicationStringModal(props) {
    const { onClose } = props;
    const formRef = useRef();
    const dispatch = useDispatch();
    const toaster = useToaster();
    const [logs, setLogs] = useState([]);
    const loggerRef = useRef(new FifoLogger());
    useEffect(() => {
        function handleLogs({ detail: { logs } }) {
            setLogs(logs.slice());
        }
        const loggerInstance = loggerRef.current;
        loggerInstance.addEventListener('change', handleLogs);
        return () => {
            loggerInstance.removeEventListener('change', handleLogs);
        };
    }, []);
    const COLUMNS = useMemo(() => [
        {
            Header: '#',
            accessor: (_, index) => index + 1,
            style: { width: '40px' },
        },
        {
            Header: 'Label',
            accessor: 'levelLabel',
            style: { width: '60px' },
        },
        {
            Header: 'Message',
            accessor: 'message',
        },
    ], []);
    const debounceChanges = useMemo(() => debounce((value) => {
        resurrect(value, { logger: loggerRef.current });
    }, 250), []);
    function publicationStringHandler({ publicationText }) {
        void (async () => {
            const hideLoading = toaster.showLoading({
                message: 'Generate spectrum from publication string in progress',
            });
            const { ranges, info: { nucleus, solvent = '', frequency }, parts, } = resurrect(publicationText, { logger: loggerRef.current });
            setTimeout(() => {
                dispatch({
                    type: 'GENERATE_SPECTRUM_FROM_PUBLICATION_STRING',
                    payload: {
                        ranges,
                        info: { nucleus, solvent, frequency, name: parts[0] },
                    },
                });
                hideLoading();
            });
            onClose();
        })();
    }
    function handleOnChange(event) {
        loggerRef.current.clear();
        const value = event.target.value;
        if (value) {
            debounceChanges(value);
        }
    }
    const isNotValid = logs.some((log) => ['error', 'fatal'].includes(log.levelLabel));
    return (_jsx(Dialog, { title: "Generate spectrum from publication string", isOpen: true, onClose: onClose, style: { width: 800, height: 500 }, children: _jsx(Formik, { innerRef: formRef, initialValues: INITIAL_VALUES, validationSchema: validationSchema, onSubmit: publicationStringHandler, children: ({ isValid }) => (_jsxs(_Fragment, { children: [_jsx(DialogBody, { children: _jsxs("div", { style: {
                                display: 'flex',
                                flexDirection: 'column',
                                height: '100%',
                            }, children: [_jsxs("div", { style: { display: 'flex', flexDirection: 'column', flex: 1 }, children: [_jsxs("p", { children: ["Paste a publication string in the text area below and click on the button ", _jsx("i", { children: "Generate spectrum" })] }), _jsx(FormikTextarea, { style: {
                                                width: '100%',
                                                flex: 1,
                                                outline: 'none',
                                                borderWidth: '1px',
                                                borderColor: '#dedede',
                                                borderRadius: '5px',
                                                resize: 'none',
                                                padding: '15px',
                                            }, name: "publicationText", className: "text-area", placeholder: "Enter publication string", onChange: handleOnChange })] }), _jsx(GroupPane, { text: "Logs", children: _jsx(ReactTable, { columns: COLUMNS, data: logs, emptyDataRowText: "No Logs", rowStyle: handleRowStyle, style: { height: '120px' } }) })] }) }), _jsx(DialogFooter, { children: _jsx("div", { style: { display: 'flex', flexDirection: 'row-reverse' }, children: _jsx(Button.Done, { onClick: () => formRef.current.submitForm(), disabled: isNotValid || !isValid, children: "Generate spectrum" }) }) })] })) }) }));
}
